import { useRef, useState } from "react";
import { Upload } from "lucide-react";

import { useUploadAvatarMutation } from "../api/avatar-hooks";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

export interface ChangeAvatarProps {
  storageName: string;
  uploadLabel?: string;
  deleteText?: string;
  onUpload: (url: string) => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
  onClose?: () => void;
  hasAvatar?: boolean;
}

export default function AvatarUpload({
  storageName,
  uploadLabel,
  deleteText,
  onUpload,
  onDelete,
  onClose,
  hasAvatar = false,
}: ChangeAvatarProps) {
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadAvatarMutation = useUploadAvatarMutation();

  function uploadAvatar(event: React.ChangeEvent<HTMLInputElement>) {
    if (!event.target.files || event.target.files.length === 0) {
      setError("You must select an image to upload.");
      return;
    }

    const file = event.target.files[0];
    const fileExt = file.name.split(".").pop();
    const filePath = `${Math.random()}.${fileExt}`;

    uploadAvatarMutation.mutate(
      { storageName, file, filePath },
      {
        onSuccess: (uploadedFilePath: string) => {
          void onUpload(uploadedFilePath);
          onClose?.();
          setError(null);
          setFileName(null);
        },
        onError: (err: Error) => {
          setError(
            err instanceof Error
              ? `Upload failed: ${err.message}`
              : "Upload failed. Please try again.",
          );
        },
      },
    );
  }

  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setFileName(e.target.files?.[0]?.name ?? null);
    uploadAvatar(e);
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    try {
      setError(null);
      await onDelete();
      onClose?.();
    } catch (err) {
      setError(
        err instanceof Error ? `Delete failed: ${err.message}` : "Delete failed. Please try again.",
      );
    }
  };

  return (
    <div className="flex flex-col gap-4">
      {!!error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-2">
        <Label htmlFor="avatar-upload">{uploadLabel}</Label>
        {/* Hidden native file input */}
        <Input
          ref={fileInputRef}
          id="avatar-upload"
          type="file"
          accept="image/*"
          onChange={handleUpload}
          disabled={uploadAvatarMutation.isPending}
          className="hidden"
        />
        {/* Custom styled trigger */}
        <Button
          type="button"
          variant="outline"
          disabled={uploadAvatarMutation.isPending}
          onClick={() => fileInputRef.current?.click()}
          className="w-full justify-start gap-2 font-normal"
        >
          <Upload className="h-4 w-4 shrink-0" />
          <span className="truncate">{fileName ?? "Choose file"}</span>
        </Button>
      </div>

      {!!uploadAvatarMutation.isPending && (
        <p className="text-muted-foreground text-sm">Uploading...</p>
      )}

      <div className="flex justify-between pt-2">
        {!!hasAvatar && !!onDelete ? (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => void handleDelete()}
            disabled={uploadAvatarMutation.isPending}
            className="flex items-center space-x-2"
          >
            {deleteText}
          </Button>
        ) : (
          <div />
        )}
        {!!onClose && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={uploadAvatarMutation.isPending}
          >
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
